import { useState, useEffect } from 'react';
import { sendMessageStream, addMessage } from '@/lib/chat-service';
import { authService } from '@/auth-service';
export function useChatSession(characterName, initialGreeting) {
    const [messages, setMessages] = useState([
        {
            id: '1',
            content: initialGreeting,
            sender: 'character',
            timestamp: new Date(),
        },
    ]);
    const [sessionId, setSessionId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [lastSyncTime, setLastSyncTime] = useState(null);
    const sendMessage = async (messageText, agenticMode) => {
        const userMessage = {
            id: Date.now().toString(),
            content: messageText,
            sender: 'user',
            timestamp: new Date(),
        };
        setMessages((prev) => [...prev, userMessage]);
        setIsLoading(true);
        try {
            if (agenticMode) {
                const characterMessageId = (Date.now() + 1).toString();
                setMessages((prev) => [...prev, {
                        id: characterMessageId,
                        content: '',
                        sender: 'character',
                        timestamp: new Date(),
                    }]);
                await sendMessageStream(messageText, {
                    sessionId,
                    character: characterName.toLowerCase(),
                    onChunk: (fullResponse) => {
                        setMessages((prev) => prev.map((msg) => msg.id === characterMessageId
                            ? { ...msg, content: fullResponse }
                            : msg));
                    },
                    onSessionId: (newSessionId) => {
                        setSessionId(newSessionId);
                    },
                    onError: (error) => {
                        console.error('Chat error:', error);
                        setMessages((prev) => prev.map((msg) => msg.id === characterMessageId
                            ? { ...msg, content: 'Sorry, there was an error. Please try again.' }
                            : msg));
                    },
                });
            }
            else {
                const result = await addMessage(messageText, sessionId);
                if (result.session_id) {
                    setSessionId(result.session_id);
                }
            }
        }
        finally {
            setIsLoading(false);
        }
    };
    const addVoiceMessage = (message) => {
        setMessages((prev) => [...prev, message]);
    };
    const updateVoiceMessage = (messageId, content) => {
        setMessages((prev) => prev.map((msg) => msg.id === messageId ? { ...msg, content } : msg));
    };
    const forceSyncSession = async () => {
        if (!sessionId) {
            console.warn('No session to sync');
            return;
        }
        setIsSyncing(true);
        try {
            const token = await authService.getIdToken();
            const response = await fetch('/api/v2/diary/session/sync', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ session_id: sessionId }),
            });
            if (!response.ok) {
                throw new Error(`Sync failed: ${response.status}`);
            }
            const result = await response.json();
            console.log('Force sync result:', result);
            setLastSyncTime(new Date());
        }
        catch (error) {
            console.error('Failed to sync session:', error);
        }
        finally {
            setIsSyncing(false);
        }
    };
    const closeSession = async () => {
        if (!sessionId)
            return;
        try {
            const token = await authService.getIdToken();
            await fetch('/api/v2/diary/session/close', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ session_id: sessionId }),
                keepalive: true, // Allow request to complete after page unload
            });
            console.log('Session closed:', sessionId);
        }
        catch (error) {
            console.error('Failed to close session:', error);
        }
    };
    // Handle window close event
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (sessionId) {
                // Attempt to close session (may not complete if browser force-quits)
                closeSession();
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            // Final cleanup when component unmounts
            if (sessionId) {
                closeSession();
            }
        };
    }, [sessionId]);
    return {
        messages,
        sessionId,
        isLoading,
        isSyncing,
        lastSyncTime,
        sendMessage,
        addVoiceMessage,
        updateVoiceMessage,
        setMessages,
        forceSyncSession,
        closeSession,
    };
}
